/**
 * Collection Filters - AJAX Filtering for McMaster-Carr style collection page
 *
 * Progressive enhancement: everything works without JS (standard form submission).
 * With JS: intercepts form submission, uses Section Rendering API for fast updates,
 * manages view modes, sort, and browser history.
 */
(function () {
  'use strict';

  const SECTION_ID = 'main-collection';
  const VIEW_STORAGE_KEY = 'bbs-view-mode';

  /** @type {AbortController|null} */
  let currentRequest = null;
  let debounceTimer = null;

  // ==================== SELECTORS ====================

  function getElements() {
    return {
      section: document.getElementById('CollectionSection'),
      form: document.getElementById('CollectionFilterForm'),
      main: document.getElementById('CollectionMain'),
      sidebar: document.querySelector('[data-collection-sidebar]'),
      filterToggle: document.querySelector('[data-filter-toggle]'),
      filterCloseButtons: document.querySelectorAll('[data-filter-close]'),
      sortSelect: document.querySelector('[data-sort-select]'),
      viewButtons: document.querySelectorAll('[data-view-mode]'),
      loading: document.querySelector('[data-collection-loading]'),
      filterBadge: document.querySelector('[data-filter-count]'),
      clearAllButton: document.querySelector('[data-clear-all]'),
      removeFilterButtons: document.querySelectorAll('[data-remove-filter]'),
      filterInputs: document.querySelectorAll('[data-filter-input]'),
      paginationLinks: document.querySelectorAll('[data-pagination-link]'),
      filterSubmit: document.querySelector('[data-filter-submit]'),
      loadMoreWrap: document.querySelector('[data-load-more-wrap]'),
      loadMoreButton: document.querySelector('[data-load-more]'),
      pagination: document.querySelector('[data-collection-pagination]'),
    };
  }

  // ==================== VIEW MODE ====================

  function initViewMode() {
    const saved = localStorage.getItem(VIEW_STORAGE_KEY);
    if (saved) {
      setViewMode(saved, false);
    }
  }

  function setViewMode(mode, save) {
    const views = document.querySelectorAll('[data-view]');
    const buttons = document.querySelectorAll('[data-view-mode]');

    views.forEach(function (view) {
      if (view.getAttribute('data-view') === mode) {
        view.removeAttribute('hidden');
      } else {
        view.setAttribute('hidden', '');
      }
    });

    buttons.forEach(function (btn) {
      const isActive = btn.getAttribute('data-view-mode') === mode;
      btn.classList.toggle('collection-page__view-btn--active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    if (save !== false) {
      localStorage.setItem(VIEW_STORAGE_KEY, mode);
    }
  }

  // ==================== MOBILE SIDEBAR ====================

  function openSidebar() {
    var sidebar = document.querySelector('[data-collection-sidebar]');
    var toggle = document.querySelector('[data-filter-toggle]');
    if (sidebar) {
      sidebar.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    }
  }

  function closeSidebar() {
    var sidebar = document.querySelector('[data-collection-sidebar]');
    var toggle = document.querySelector('[data-filter-toggle]');
    if (sidebar) {
      sidebar.classList.remove('is-open');
      document.body.style.overflow = '';
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }
  }

  // ==================== URL BUILDING ====================

  function buildUrlFromForm(form) {
    var formData = new FormData(form);
    var params = new URLSearchParams();

    for (var pair of formData.entries()) {
      if (pair[1] !== '' && pair[1] !== null && pair[1] !== undefined) {
        params.append(pair[0], pair[1]);
      }
    }

    // Include sort from the toolbar select (it's associated via form attribute)
    var sortSelect = document.querySelector('[data-sort-select]');
    if (sortSelect && sortSelect.value) {
      // Remove any existing sort_by from hidden inputs
      params.delete('sort_by');
      params.append('sort_by', sortSelect.value);
    }

    var baseUrl = form.getAttribute('action') || window.location.pathname;
    var paramString = params.toString();
    return paramString ? baseUrl + '?' + paramString : baseUrl;
  }

  // ==================== SECTION RENDERING ====================

  function showLoading() {
    var loading = document.querySelector('[data-collection-loading]');
    var views = document.querySelectorAll('[data-view]');

    if (loading) loading.setAttribute('aria-hidden', 'false');
    views.forEach(function (v) { v.style.opacity = '0.3'; });
  }

  function hideLoading() {
    var loading = document.querySelector('[data-collection-loading]');
    var views = document.querySelectorAll('[data-view]');

    if (loading) loading.setAttribute('aria-hidden', 'true');
    views.forEach(function (v) { v.style.opacity = ''; });
  }

  function fetchAndRenderSection(url, pushState) {
    // Abort any in-flight request
    if (currentRequest) {
      currentRequest.abort();
    }
    currentRequest = new AbortController();

    showLoading();

    var sectionUrl = url + (url.indexOf('?') !== -1 ? '&' : '?') + 'section_id=' + SECTION_ID;

    fetch(sectionUrl, { signal: currentRequest.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then(function (html) {
        var temp = document.createElement('div');
        temp.innerHTML = html;

        // Replace the entire section content
        var section = document.getElementById('CollectionSection');
        var newSection = temp.querySelector('#CollectionSection');

        if (section && newSection) {
          section.innerHTML = newSection.innerHTML;
        }

        // Update browser URL
        if (pushState !== false) {
          window.history.pushState({ collectionUrl: url }, '', url);
        }

        // Re-apply saved view mode
        var savedView = localStorage.getItem(VIEW_STORAGE_KEY);
        if (savedView) {
          setViewMode(savedView, false);
        }

        // Re-bind events on new DOM
        bindEvents();

        hideLoading();
        currentRequest = null;

        // Scroll to top of products
        var main = document.getElementById('CollectionMain');
        if (main) {
          main.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      })
      .catch(function (error) {
        if (error.name === 'AbortError') return;
        console.error('Collection filter fetch error:', error);
        hideLoading();
        currentRequest = null;
        // Fallback: navigate normally
        window.location.href = url;
      });
  }

  // ==================== EVENT HANDLERS ====================

  function handleFilterFormSubmit(e) {
    e.preventDefault();
    var form = document.getElementById('CollectionFilterForm');
    if (!form) return;
    var url = buildUrlFromForm(form);
    fetchAndRenderSection(url, true);
    closeSidebar();
  }

  function handleFilterInputChange(e) {
    var input = e.target;
    // For price range inputs, debounce
    if (input.hasAttribute('data-filter-price')) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        var form = document.getElementById('CollectionFilterForm');
        if (form) {
          var url = buildUrlFromForm(form);
          fetchAndRenderSection(url, true);
        }
      }, 600);
    } else {
      // Checkbox changes: submit immediately
      var form = document.getElementById('CollectionFilterForm');
      if (form) {
        var url = buildUrlFromForm(form);
        fetchAndRenderSection(url, true);
      }
    }
  }

  function buildSortUrl(sortValue) {
    // Start from the current URL params so active filters and pagination
    // context are preserved, then set/replace sort_by.
    var params = new URLSearchParams(window.location.search);

    if (sortValue) {
      params.set('sort_by', sortValue);
    } else {
      params.delete('sort_by');
    }

    // Changing sort should return to the first page of results.
    params.delete('page');

    var baseUrl = window.location.pathname;
    var paramString = params.toString();
    return paramString ? baseUrl + '?' + paramString : baseUrl;
  }

  function handleSortChange(e) {
    var select = (e && e.currentTarget) || document.querySelector('[data-sort-select]');
    if (!select) return;
    var url = buildSortUrl(select.value);
    fetchAndRenderSection(url, true);
  }

  function handleClearAll(e) {
    e.preventDefault();
    var href = e.currentTarget.getAttribute('href');
    if (href) {
      fetchAndRenderSection(href, true);
    }
  }

  function handleRemoveFilter(e) {
    e.preventDefault();
    var href = e.currentTarget.getAttribute('href');
    if (href) {
      fetchAndRenderSection(href, true);
    }
  }

  function handlePaginationClick(e) {
    e.preventDefault();
    var href = e.currentTarget.getAttribute('href');
    if (href) {
      fetchAndRenderSection(href, true);
    }
  }

  function handlePopState(e) {
    var url = e.state && e.state.collectionUrl ? e.state.collectionUrl : window.location.href;
    fetchAndRenderSection(url, false);
  }

  // ==================== LOAD MORE ====================

  // Loading state flag shared with handleLoadMore
  var loadMoreInFlight = false;

  // Container selectors per view. Each entry: where new items live in the
  // fetched HTML, and where to append them in the live DOM.
  var LOAD_MORE_VIEWS = [
    { container: '[data-view="grid"]', itemSelector: ':scope > .pc-card' },
    { container: '[data-view="list"]', itemSelector: ':scope > .product-list-item' },
    { container: '[data-view="table"] .product-table__body', itemSelector: ':scope > .product-table__row' },
  ];

  // Build the next-page URL from the CURRENT location so active filters and
  // sort params are preserved, then set page= to the requested page.
  function buildLoadMoreUrl(nextPage) {
    var params = new URLSearchParams(window.location.search);
    params.set('page', nextPage);
    var baseUrl = window.location.pathname;
    return baseUrl + '?' + params.toString();
  }

  function setLoadMoreLoading(isLoading) {
    var wrap = document.querySelector('[data-load-more-wrap]');
    var button = document.querySelector('[data-load-more]');
    if (!button) return;
    button.disabled = isLoading;
    if (wrap) wrap.classList.toggle('is-loading', isLoading);
  }

  function handleLoadMore(e) {
    if (e) e.preventDefault();
    if (loadMoreInFlight) return;

    var wrap = document.querySelector('[data-load-more-wrap]');
    if (!wrap) return;

    var nextPage = parseInt(wrap.getAttribute('data-next-page'), 10);
    var totalPages = parseInt(wrap.getAttribute('data-total-pages'), 10);
    if (!nextPage) return;

    loadMoreInFlight = true;
    setLoadMoreLoading(true);

    var url = buildLoadMoreUrl(nextPage);
    var sectionUrl = url + '&section_id=' + SECTION_ID;

    fetch(sectionUrl)
      .then(function (response) {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then(function (html) {
        var temp = document.createElement('div');
        temp.innerHTML = html;

        // Append items into each view container so all views stay in sync.
        LOAD_MORE_VIEWS.forEach(function (view) {
          var target = document.querySelector(view.container);
          var source = temp.querySelector(view.container);
          if (!target || !source) return;
          var newItems = source.querySelectorAll(view.itemSelector);
          newItems.forEach(function (item) {
            target.appendChild(item);
          });
        });

        // Advance to the following page, or remove the button on the last page.
        if (nextPage < totalPages) {
          wrap.setAttribute('data-next-page', String(nextPage + 1));
          setLoadMoreLoading(false);
        } else if (wrap.parentNode) {
          wrap.parentNode.removeChild(wrap);
        }

        loadMoreInFlight = false;
      })
      .catch(function (error) {
        console.error('Load more fetch error:', error);
        setLoadMoreLoading(false);
        loadMoreInFlight = false;
      });
  }

  // When JS-driven Load More is present, hide the numbered pagination fallback.
  function syncPaginationVisibility() {
    var wrap = document.querySelector('[data-load-more-wrap]');
    var pagination = document.querySelector('[data-collection-pagination]');
    if (pagination && wrap) {
      pagination.setAttribute('hidden', '');
    } else if (pagination) {
      pagination.removeAttribute('hidden');
    }
  }

  // ==================== EVENT BINDING ====================

  function bindEvents() {
    var els = getElements();

    // Filter form submit
    if (els.form) {
      els.form.removeEventListener('submit', handleFilterFormSubmit);
      els.form.addEventListener('submit', handleFilterFormSubmit);
    }

    // Filter input changes (auto-submit)
    document.querySelectorAll('[data-filter-input]').forEach(function (input) {
      input.removeEventListener('change', handleFilterInputChange);
      input.addEventListener('change', handleFilterInputChange);
    });

    // Price inputs: also listen for keyup with debounce
    document.querySelectorAll('[data-filter-price]').forEach(function (input) {
      input.removeEventListener('input', handleFilterInputChange);
      input.addEventListener('input', handleFilterInputChange);
    });

    // Sort dropdown
    var sortSelect = document.querySelector('[data-sort-select]');
    if (sortSelect) {
      sortSelect.removeEventListener('change', handleSortChange);
      sortSelect.addEventListener('change', handleSortChange);
    }

    // View mode buttons
    document.querySelectorAll('[data-view-mode]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        setViewMode(btn.getAttribute('data-view-mode'), true);
      });
    });

    // Mobile sidebar toggle
    var filterToggle = document.querySelector('[data-filter-toggle]');
    if (filterToggle) {
      filterToggle.removeEventListener('click', openSidebar);
      filterToggle.addEventListener('click', openSidebar);
    }

    // Sidebar close buttons
    document.querySelectorAll('[data-filter-close]').forEach(function (btn) {
      btn.removeEventListener('click', closeSidebar);
      btn.addEventListener('click', closeSidebar);
    });

    // Clear all
    var clearAll = document.querySelector('[data-clear-all]');
    if (clearAll) {
      clearAll.removeEventListener('click', handleClearAll);
      clearAll.addEventListener('click', handleClearAll);
    }

    // Remove individual filters
    document.querySelectorAll('[data-remove-filter]').forEach(function (btn) {
      btn.removeEventListener('click', handleRemoveFilter);
      btn.addEventListener('click', handleRemoveFilter);
    });

    // Pagination links
    document.querySelectorAll('[data-pagination-link]').forEach(function (link) {
      link.removeEventListener('click', handlePaginationClick);
      link.addEventListener('click', handlePaginationClick);
    });

    // Load More button
    var loadMoreBtn = document.querySelector('[data-load-more]');
    if (loadMoreBtn) {
      loadMoreBtn.removeEventListener('click', handleLoadMore);
      loadMoreBtn.addEventListener('click', handleLoadMore);
    }

    // Hide numbered pagination when JS-driven Load More is active
    syncPaginationVisibility();
  }

  // ==================== INITIALIZATION ====================

  function applyDefaultSort() {
    // When the shopper has not chosen a sort (no sort_by in the URL) and the
    // merchant has configured a storefront default, apply it. If no default is
    // set, do nothing so Shopify uses the collection's own admin sort order.
    var section = document.getElementById('CollectionSection');
    if (!section) return;

    var defaultSort = section.getAttribute('data-default-sort');
    if (!defaultSort) return;

    var currentParams = new URLSearchParams(window.location.search);
    if (currentParams.has('sort_by')) return;

    var select = document.querySelector('[data-sort-select]');
    if (select) {
      select.value = defaultSort;
    }

    var url = buildSortUrl(defaultSort);
    fetchAndRenderSection(url, false);
  }

  function init() {
    // Only enhance if the collection section exists
    if (!document.getElementById('CollectionSection')) return;

    bindEvents();
    initViewMode();
    applyDefaultSort();

    // Handle browser back/forward
    window.addEventListener('popstate', handlePopState);

    // Store initial state
    window.history.replaceState(
      { collectionUrl: window.location.href },
      '',
      window.location.href
    );

    // Hide the manual submit button when JS is active (auto-submit handles it)
    var submitBtn = document.querySelector('[data-filter-submit]');
    if (submitBtn) {
      submitBtn.style.display = 'none';
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
